import type { DoctorProfile, PracticeLocation } from './types'

function getActiveLocation(doc: DoctorProfile, idx: number): PracticeLocation {
  const locs = doc.locations || []
  return locs[idx] || locs[0] || { label: '', address: '', phone: '', fax: '' }
}

interface DoctorSelectorProps {
  doctors: DoctorProfile[]
  selectedDoctor: DoctorProfile | null
  selectedDoctorId: string | null
  onSelectDoctor: (id: string) => void
  onEditDoctor: (doctor: DoctorProfile) => void
  onAddDoctor: () => void
  selectedLocationIdx: number
  onSelectLocation: (idx: number) => void
}

export default function DoctorSelector({
  doctors,
  selectedDoctor,
  selectedDoctorId,
  onSelectDoctor,
  onEditDoctor,
  onAddDoctor,
  selectedLocationIdx,
  onSelectLocation,
}: DoctorSelectorProps) {
  const activeLocation = selectedDoctor ? getActiveLocation(selectedDoctor, selectedLocationIdx) : null

  if (!selectedDoctor && doctors.length === 0) {
    return (
      <button onClick={onAddDoctor}
        className="w-full bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl border-2 border-dashed border-teal-300 p-5 text-center hover:border-teal-400 hover:from-teal-100 transition-all group">
        <div className="w-10 h-10 rounded-xl bg-teal-100 text-teal-500 flex items-center justify-center text-lg font-bold mx-auto mb-2 group-hover:bg-teal-200 transition-colors">+</div>
        <p className="text-sm font-bold text-teal-700">Add Your Doctor Profile</p>
        <p className="text-[11px] text-slate-400 mt-1">Auto-fill provider info on every LMN you generate</p>
      </button>
    )
  }

  if (!selectedDoctor) return null

  return (
    <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl border border-teal-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-bold text-teal-600 uppercase tracking-wider">Writing LMN as</span>
        <button onClick={() => onEditDoctor(selectedDoctor)}
          className="text-[10px] text-slate-400 hover:text-blue-500 font-semibold transition-colors flex items-center gap-1">
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
          Edit
        </button>
      </div>

      {/* Active doctor display */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-teal-500 text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
          {selectedDoctor.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-800 truncate">{selectedDoctor.name}</p>
          <p className="text-[11px] text-slate-500 truncate">{selectedDoctor.specialty} &bull; NPI: {selectedDoctor.npi}</p>
          <p className="text-[11px] text-slate-400 truncate">{selectedDoctor.practiceName}</p>
        </div>
      </div>

      {/* Switch doctor — only show if multiple doctors on account */}
      {doctors.length > 1 && (
        <div className="mt-3 pt-3 border-t border-teal-100">
          <label className="text-[10px] font-bold text-teal-600 uppercase tracking-wider mb-1.5 block">Switch Doctor</label>
          <div className="space-y-1.5">
            {doctors.filter(d => d.id !== selectedDoctorId).map(doc => (
              <button key={doc.id}
                onClick={() => onSelectDoctor(doc.id)}
                className="w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-center gap-2.5 bg-white border border-slate-200 text-slate-600 hover:border-teal-300 hover:bg-teal-50/30">
                <div className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center text-[10px] font-bold flex-shrink-0">
                  {doc.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-slate-700">{doc.name}</span>
                  <p className="text-[10px] text-slate-400 truncate">{doc.specialty}</p>
                </div>
                <svg className="w-3.5 h-3.5 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Location selector — only if multiple locations */}
      {selectedDoctor.locations.length > 1 && (
        <div className="mt-3 pt-3 border-t border-teal-100">
          <label className="text-[10px] font-bold text-teal-600 uppercase tracking-wider mb-1.5 block">Location for this LMN</label>
          <div className="space-y-1.5">
            {selectedDoctor.locations.map((loc, idx) => (
              <button key={idx} onClick={() => onSelectLocation(idx)}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all flex items-start gap-2 ${
                  idx === selectedLocationIdx
                    ? 'bg-teal-100 border border-teal-300 text-teal-800'
                    : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-200 hover:bg-teal-50/30'
                }`}>
                <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  idx === selectedLocationIdx ? 'border-teal-500' : 'border-slate-300'
                }`}>
                  {idx === selectedLocationIdx && <span className="w-2 h-2 rounded-full bg-teal-500" />}
                </span>
                <div className="min-w-0">
                  <span className="font-semibold">{loc.label || `Location ${idx + 1}`}</span>
                  {loc.address && <p className="text-[10px] text-slate-400 truncate mt-0.5">{loc.address}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Show active location details — only if single location */}
      {selectedDoctor.locations.length <= 1 && activeLocation && activeLocation.address && (
        <div className="mt-2 text-[10px] text-slate-400 truncate">
          <span className="text-slate-300">&#9679;</span> {activeLocation.address}
        </div>
      )}
    </div>
  )
}
